import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CardPhotoCarousel } from "../components/CardPhotoCarousel";
import { Funnel, MagnifyingGlass, Camera, Sparkle, ArrowLeft, SortAscending } from "@phosphor-icons/react";

// Chronological rank for "Periodo storico" sorting
const PERIOD_RANKS = [
  ["preistor", 0], ["protostor", 1], ["età del bronzo", 2], ["età del ferro", 3],
  ["etrusc", 4], ["celtic", 4], ["grec", 4], ["roman", 5], ["antic", 5],
  ["tardoantic", 6], ["bizantin", 7], ["altomediev", 8], ["medioev", 9], ["mediev", 9],
  ["rinascim", 10], ["modern", 11], ["napoleon", 12], ["contempor", 13],
];
const periodRank = (p) => {
  const s = (p || "").toLowerCase();
  if (!s) return 99;
  for (const [k, r] of PERIOD_RANKS) if (s.includes(k)) return r;
  return 50;
};

const SORT_KEY = "reperti-catalog-sort";
const SORT_OPTIONS = [
  { id: "codice", label: "Ordina: Codice (1 →)" },
  { id: "codice_desc", label: "Ordina: Codice (→ 1)" },
  { id: "valore", label: "Ordina: Valore (€ più alto)" },
  { id: "data", label: "Ordina: Data (più recente)" },
  { id: "periodo", label: "Ordina: Periodo storico" },
];

const codeKey = (it) => {
  const m = ((it.inventory_code || "").trim().toUpperCase()).match(/^([A-Z]+)(\d+)$/);
  return m ? { prefix: m[1], num: parseInt(m[2], 10) } : null;
};

const sortItems = (items, sortBy) => {
  const arr = [...items];
  if (sortBy === "valore") {
    arr.sort((a, b) => {
      const va = a.identification?.valore_stimato_eur;
      const vb = b.identification?.valore_stimato_eur;
      return (typeof vb === "number" ? vb : -1) - (typeof va === "number" ? va : -1);
    });
  } else if (sortBy === "data") {
    arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  } else if (sortBy === "codice_desc") {
    arr.sort((a, b) => {
      const ka = codeKey(a);
      const kb = codeKey(b);
      if (!ka && !kb) return 0;
      if (!ka) return 1;
      if (!kb) return -1;
      if (ka.prefix !== kb.prefix) return ka.prefix.localeCompare(kb.prefix);
      return kb.num - ka.num;
    });
  } else if (sortBy === "periodo") {
    arr.sort((a, b) => periodRank(a.identification?.periodo) - periodRank(b.identification?.periodo));
  }
  return arr; // "codice": keep backend order (numeric by inventory code)
};

const Empty = () => (
  <div
    data-testid="catalog-empty"
    className="border border-dashed border-[#1E2520]/15 p-10 md:p-16 text-center bg-[#F0EBE1]/40"
  >
    <Sparkle weight="duotone" size={42} className="mx-auto text-[#9C5B42] mb-4" />
    <h3 className="font-serif text-3xl text-[#1E2520] mb-2">Nessun reperto in questa categoria</h3>
    <p className="text-[#5C635A] mb-6 max-w-md mx-auto">
      Scatta o carica le foto del tuo prossimo reperto. L'AI lo classificherà automaticamente.
    </p>
    <Link to="/scan" className="btn-primary inline-flex items-center gap-2" data-testid="empty-scan-btn">
      <Camera weight="duotone" size={18} /> Identifica un reperto
    </Link>
  </div>
);

const ArtifactCard = ({ item }) => {
  const ident = item.identification || {};
  return (
    <Link
      to={`/artifact/${item.id}`}
      className="card group overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300 relative"
      data-testid={`artifact-card-${item.id}`}
    >
      {item.inventory_code && (
        <span
          data-testid={`code-badge-${item.inventory_code}`}
          className="absolute top-3 left-3 z-10 bg-[#1E2520] text-[#F9F8F6] font-mono text-xs tracking-[0.15em] px-2.5 py-1.5 rounded-sm shadow-md"
        >
          {item.inventory_code}
        </span>
      )}
      <CardPhotoCarousel
        photos={item.photos || []}
        alt={ident.nome || "Reperto"}
        testId={`card-carousel-${item.id}`}
      />
      <div className="p-5">
        <div className="label-eyebrow mb-2">{ident.categoria_dettaglio || ident.categoria || "Reperto"}</div>
        <h3 className="font-serif text-2xl leading-tight text-[#1E2520] mb-1 line-clamp-2">
          {ident.nome || "Reperto senza nome"}
        </h3>
        <p className="text-sm text-[#5C635A]">{ident.periodo}{ident.datazione ? ` • ${ident.datazione}` : ""}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-[#5C635A]">{ident.materiale}</span>
          {typeof ident.valore_stimato_eur === "number" && (
            <span className="font-serif text-xl text-[#9C5B42]">
              € {ident.valore_stimato_eur.toLocaleString("it-IT")}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default function Catalog() {
  const { category } = useParams(); // 'tutto' or category id
  const isAll = !category || category === "tutto";
  const decoded = category && !isAll ? decodeURIComponent(category) : null;

  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ periodi: [], materiali: [], rarita: [], conservazione: [], codici: [] });
  // URL query params drive the initial filter state so pages like /stats
  // can link straight into a pre-filtered catalog (e.g. ?materiale=Argento).
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilters, setActiveFilters] = useState({
    periodo: searchParams.get("periodo") || "",
    materiale: searchParams.get("materiale") || "",
    rarita: searchParams.get("rarita") || "",
    conservazione: searchParams.get("conservazione") || "",
    codice: searchParams.get("codice") || "",
  });
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(SORT_KEY) || "codice");

  useEffect(() => { localStorage.setItem(SORT_KEY, sortBy); }, [sortBy]);

  // Keep the URL in sync with the active filters — makes the page shareable
  // and lets users use browser back/forward to navigate through their picks.
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(activeFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
    if (q) params.set("q", q);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line
  }, [activeFilters, q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isAll) params.categoria = decoded;
      if (activeFilters.periodo) params.periodo = activeFilters.periodo;
      if (activeFilters.materiale) params.materiale = activeFilters.materiale;
      if (activeFilters.rarita) params.rarita = activeFilters.rarita;
      if (activeFilters.conservazione) params.conservazione = activeFilters.conservazione;
      const q2 = q || activeFilters.codice;
      if (q2) params.q = q2;
      const [list, fop] = await Promise.all([
        api.get("/artifacts", { params }),
        api.get("/artifacts/filters"),
      ]);
      setItems(list.data || []);
      setFilters(fop.data || { periodi: [], materiali: [], rarita: [], conservazione: [], codici: [] });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFilters, category]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const title = isAll ? "Tutto il catalogo" : decoded;
  const codeFilteredItems = activeFilters.codice
    ? items.filter((it) => (it.inventory_code || "").toUpperCase() === activeFilters.codice.toUpperCase())
    : items;
  const sortedItems = useMemo(() => sortItems(codeFilteredItems, sortBy), [codeFilteredItems, sortBy]);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 fade-in">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-[#5C635A] hover:text-[#9C5B42] mb-6"
        data-testid="back-to-home"
      >
        <ArrowLeft size={16} /> Tutte le categorie
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <div className="label-eyebrow mb-3">{isAll ? "Catalogo" : "Categoria"}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1E2520]">
            {title}
          </h1>
          <p className="text-[#5C635A] mt-2">
            {codeFilteredItems.length} repert{codeFilteredItems.length === 1 ? "o" : "i"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 md:p-5 mb-8 flex flex-col md:flex-row gap-3 md:items-center" data-testid="filters-bar">
        <form onSubmit={onSearch} className="flex-1 flex items-center gap-2">
          <MagnifyingGlass weight="duotone" size={20} className="text-[#5C635A] shrink-0" />
          <input
            data-testid="search-input"
            className="input-base bg-transparent border-0 focus:ring-0"
            placeholder="Cerca per nome, descrizione, codice (es: M3)..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </form>
        <div className="flex items-center gap-2 flex-wrap">
          <SortAscending weight="duotone" size={18} className="text-[#5C635A]" />
          <select
            data-testid="sort-select"
            className="input-base py-2"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <Funnel weight="duotone" size={18} className="text-[#5C635A]" />
          <select
            data-testid="filter-codice"
            className="input-base py-2"
            value={activeFilters.codice}
            onChange={(e) => setActiveFilters((s) => ({ ...s, codice: e.target.value }))}
          >
            <option value="">Tutti i codici</option>
            {filters.codici
              .filter((c) => isAll || (decoded && c.startsWith((items[0]?.inventory_code || "")[0] || c[0])))
              .map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="filter-periodo"
            className="input-base py-2"
            value={activeFilters.periodo}
            onChange={(e) => setActiveFilters((s) => ({ ...s, periodo: e.target.value }))}
          >
            <option value="">Tutti i periodi</option>
            {filters.periodi.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="filter-materiale"
            className="input-base py-2"
            value={activeFilters.materiale}
            onChange={(e) => setActiveFilters((s) => ({ ...s, materiale: e.target.value }))}
          >
            <option value="">Tutti i materiali</option>
            {filters.materiali.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="filter-rarita"
            className="input-base py-2"
            value={activeFilters.rarita}
            onChange={(e) => setActiveFilters((s) => ({ ...s, rarita: e.target.value }))}
          >
            <option value="">Tutte le rarità</option>
            {(filters.rarita || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="filter-conservazione"
            className="input-base py-2"
            value={activeFilters.conservazione}
            onChange={(e) => setActiveFilters((s) => ({ ...s, conservazione: e.target.value }))}
          >
            <option value="">Ogni conservazione</option>
            {(filters.conservazione || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {(activeFilters.periodo || activeFilters.materiale || activeFilters.rarita || activeFilters.conservazione || activeFilters.codice || q) && (
            <button
              type="button"
              onClick={() => {
                setActiveFilters({ periodo: "", materiale: "", rarita: "", conservazione: "", codice: "" });
                setQ("");
              }}
              className="btn-ghost text-xs px-3 py-2"
              data-testid="clear-filters-btn"
            >
              Pulisci filtri
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card overflow-hidden">
              <div className="aspect-square scanning" />
              <div className="p-5 space-y-2">
                <div className="h-3 w-20 scanning" />
                <div className="h-6 w-3/4 scanning" />
                <div className="h-3 w-1/2 scanning" />
              </div>
            </div>
          ))}
        </div>
      ) : codeFilteredItems.length === 0 ? (
        <Empty />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="catalog-grid">
          {sortedItems.map((it) => <ArtifactCard key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
}
